import { useCallback, useEffect, useRef } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useDragDismiss } from './drag.js';

export interface BannerTone {
  /** Text and border. */
  fg: string;
  /** Solid: nothing behind a banner ever shows through it. */
  bg: string;
  border?: string;
}

/**
 * A banner that drops in from the top, like an iPhone notification.
 *
 * - Flick it UP to put it away (`onDismiss`). Pulling it down stretches and springs back, or,
 *   when there is a card behind it (`onOpen`), opens that card.
 * - Tap it to open its card, or, for a plain message, to put it away.
 * - `autoHideMs` puts it away by itself; the clock stops while a finger is on it.
 * - Without `onDismiss` it stays until whatever it is about is over (a live alarm).
 */
export function TopBanner({
  tone,
  children,
  onDismiss,
  onOpen,
  autoHideMs,
  tapToDismiss = true,
  compact = false,
  role = 'status',
  style,
}: {
  tone: BannerTone;
  children: ReactNode;
  onDismiss?: (() => void) | undefined;
  onOpen?: (() => void) | undefined;
  autoHideMs?: number | undefined;
  tapToDismiss?: boolean;
  /** A one-line bar rather than a card. */
  compact?: boolean;
  role?: 'status' | 'alert';
  style?: CSSProperties;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const left = useRef(autoHideMs ?? 0);
  const since = useRef(0);

  const stop = () => {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
    left.current = Math.max(600, left.current - (Date.now() - since.current));
  };

  const drag = useDragDismiss<HTMLDivElement>({
    direction: 'up',
    onDismiss,
    onExpand: onOpen,
    distance: 40,
    flick: 0.3,
    onActive: (active) => (active ? stop() : start()),
  });

  // The clock must not restart every time the banner re-renders (its callbacks are new each
  // time), or it would never run out: it reads them through refs and depends on the length only.
  const canHide = useRef(false);
  canHide.current = Boolean(onDismiss);
  const dismiss = useRef(drag.dismiss);
  dismiss.current = drag.dismiss;
  const start = useCallback(() => {
    if (!autoHideMs || !canHide.current || timer.current !== null) return;
    since.current = Date.now();
    timer.current = setTimeout(() => {
      timer.current = null;
      dismiss.current();
    }, left.current);
  }, [autoHideMs]);

  useEffect(() => {
    start();
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [start]);

  const tap = (e: MouseEvent<HTMLDivElement>) => {
    if (drag.wasDrag()) return;
    const button = (e.target as HTMLElement).closest('button, [data-nodrag]');
    if (button && button !== e.currentTarget) return;
    if (onOpen) onOpen();
    else if (tapToDismiss && onDismiss) drag.dismiss();
  };

  const tappable = Boolean(onOpen || (tapToDismiss && onDismiss));

  return (
    <div
      ref={(el) => {
        drag.ref.current = el;
        drag.handleRef(el);
      }}
      {...drag.bind}
      onClick={tap}
      role={role}
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
      className="cc-banner"
      style={{
        position: 'relative',
        boxSizing: 'border-box',
        minHeight: 48,
        borderRadius: compact ? 14 : 18,
        padding: compact ? '12px 14px 14px' : '14px 16px 16px',
        background: tone.bg,
        border: `1px solid ${tone.border ?? tone.fg}`,
        color: tone.fg,
        boxShadow: '0 10px 30px rgba(0,0,0,.55)',
        touchAction: 'none',
        cursor: tappable ? 'pointer' : undefined,
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
      {/* The little bar iOS puts under a notification: this can be pushed back up. */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 5,
          width: 36,
          height: 4,
          marginLeft: -18,
          borderRadius: 2,
          background: tone.fg,
          opacity: onDismiss ? 0.4 : 0.18,
        }}
      />
    </div>
  );
}
